<template>
  <div
    v-if="hasAppearances"
    ref="colorsContainer"
    class="colors"
    :style="{ gap: `${circleGap}px` }"
  >
    <ColorCircle
      v-for="(appearance, index) in appearances"
      :key="appearance.id"
      :ref="(el) => setCircleRef(el, index)"
      :color="appearance.color"
      :size="circleSize"
      :name="appearance.name"
      :style="{
        display: index < visibleCircleCount ? 'block' : 'none',
      }"
    />
    <strong
      v-if="hiddenCircleCount > 0"
      ref="colorIndicator"
      class="color-indicator"
    >
      +{{ hiddenCircleCount }} {{ $t('GENERAL.COLORS') }}
    </strong>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  ref,
  onMounted,
  onUnmounted,
  nextTick,
  ComponentPublicInstance,
} from 'vue';
import ColorCircle from 'commons/colorCircle/ColorCircle.vue';

interface Appearance {
  id: number;
  name: string;
  color: string;
}

interface Props {
  appearances: Appearance[];
  maxColors?: number;
}

const props = defineProps<Props>();
const circleSize = 16;
const circleGap = 4;

const colorsContainer = ref<HTMLElement | null>(null);
const colorIndicator = ref<HTMLElement | null>(null);
const circleRefs = ref<(HTMLElement | null)[]>([]);
const visibleCircleCount = ref(0);
const resizeObserver = ref<ResizeObserver | null>(null);

const hasAppearances = computed(() => {
  return props.appearances && props.appearances.length > 0;
});

const hiddenCircleCount = computed(() => {
  return Math.max(0, props.appearances.length - visibleCircleCount.value);
});

const setCircleRef = (
  el: Element | ComponentPublicInstance | null,
  index: number,
) => {
  if (el && '$el' in el) {
    circleRefs.value[index] = el.$el as HTMLElement;
  } else if (el) {
    circleRefs.value[index] = el as HTMLElement;
  }
};

const calculateVisibleCircles = async () => {
  if (!colorsContainer.value) {
    return;
  }

  await nextTick();

  const containerWidth = colorsContainer.value.offsetWidth;
  const totalCount = props.appearances.length;

  const maxPossibleCircles = props.maxColors
    ? Math.min(props.maxColors, totalCount)
    : totalCount;

  const spaceNeededForAllCircles =
    maxPossibleCircles * circleSize + (maxPossibleCircles - 1) * circleGap;

  if (containerWidth >= spaceNeededForAllCircles) {
    // all circles fit, no indicator needed
    visibleCircleCount.value = maxPossibleCircles;
    return;
  }

  // we need an indicator, so measure its width
  // temporarily show one circle to render the indicator for measurement
  visibleCircleCount.value = 1;
  await nextTick();

  let indicatorWidth = 70; // fallback width
  if (colorIndicator.value) {
    indicatorWidth = colorIndicator.value.offsetWidth;
  }

  // calculate how many circles fit with indicator
  const availableWidthForCircles = containerWidth - indicatorWidth; // - circleGap
  const maxCirclesWithIndicator = Math.floor(
    availableWidthForCircles / (circleSize + circleGap),
  );

  // ensure we don't exceed the maximum possible circles and show at least 1
  const finalCircleCount = Math.max(
    1,
    Math.min(maxCirclesWithIndicator, maxPossibleCircles - 1),
  );

  visibleCircleCount.value = finalCircleCount;
};

onMounted(() => {
  if (colorsContainer.value) {
    resizeObserver.value = new ResizeObserver(() => {
      calculateVisibleCircles();
    });
    resizeObserver.value.observe(colorsContainer.value);

    calculateVisibleCircles();
  }
});

onUnmounted(() => {
  if (resizeObserver.value) {
    resizeObserver.value.disconnect();
  }
});
</script>

<style lang="scss" scoped>
.colors {
  display: flex;
  width: 100%;
  align-items: center;
  margin-top: 10px;

  :deep(.color) {
    margin: 0;
  }

  strong {
    text-wrap: nowrap;
  }
}
</style>
